import { z } from 'zod';
import { payableStageSchema, type PayableStage } from '../constants/stages.js';
import { shopDaySchema } from '../reports/date-ranges.js';
import {
  assignmentPrioritySchema,
  estimatedMinutesSchema,
  otMinutesSchema,
  DEFAULT_ASSIGNMENT_PRIORITY,
} from './assignments.js';

/**
 * "Can you have this by the 24th?" — the question a shop currently answers by feel.
 *
 * THIS CONTRACT DESCRIBES A CALCULATION AND NOT A RECORD. Nothing here is stored: there is no
 * capacity document, no capacity collection and no capacity counter. Every number on the
 * response is derived at request time from things other modules already own — the roster, the
 * shop's enabled workflow stages, its attendance settings and its open assignments. The service
 * behind it says the same thing at more length, and says why a materialised calendar was
 * rejected.
 *
 * EVERY DURATION HERE IS A WHOLE NUMBER OF MINUTES, on the request and on the response alike —
 * the rule `schemas/assignments.ts` set for the plan fields these tasks mirror. There is no
 * float hour anywhere on the wire. "3h 20m" is a display format, and the screen that renders it
 * owns it.
 */

/* -------------------------------------------------------------------------------------------
 * The numbers the engine is allowed to assume
 * ---------------------------------------------------------------------------------------- */

/**
 * What an OPEN assignment carrying no estimate is charged.
 *
 * `estimatedMinutes` is nullable and defaults to null, so most rows already in the collection
 * carry nothing. Charging such a row zero minutes would be a lie the shop pays for: the work is
 * on somebody's table whether or not anybody typed a number against it, and a capacity answer
 * that pretends otherwise promises a delivery date the shop cannot keep. Two hours is a
 * deliberately modest guess — enough that unestimated work visibly costs capacity, small enough
 * that a backlog of them does not make every date impossible. The response reports how many rows
 * were charged this way and what figure was assumed, so the screen can caveat the verdict.
 *
 * A PROPOSED task, by contrast, is never guessed at: the request schema requires its estimate.
 */
export const UNESTIMATED_ASSIGNMENT_MINUTES = 120;

/**
 * How far past the requested day the engine will walk looking for a date that works.
 *
 * Bounded on purpose. Past this many days the honest answer is "not in any timeframe worth
 * quoting", and `earliestDeliveryDay` comes back `null` rather than a fabricated date three
 * months out that nobody would have promised anyway. The same bound caps how far ahead a
 * delivery day may be asked about at all, so no single request can ask the engine to build a
 * calendar of years.
 */
export const CAPACITY_HORIZON_DAYS = 90;

/** One request is a job on the counter, not a production plan for the quarter. */
export const CAPACITY_MAX_TASKS = 50;

/** Sunday. What a shop that says nothing about its week is assumed to shut on. */
export const DEFAULT_WEEKLY_OFF_DAYS: readonly number[] = Object.freeze([0]);

/** A weekday as `Date.getDay()` numbers them — 0 is Sunday. */
export const weekdaySchema = z
  .number()
  .int('Give a weekday as a whole number')
  .min(0, 'Weekdays run 0 (Sunday) to 6 (Saturday)')
  .max(6, 'Weekdays run 0 (Sunday) to 6 (Saturday)');

/* -------------------------------------------------------------------------------------------
 * The request
 * ---------------------------------------------------------------------------------------- */

export const CAPACITY_TASK_LABEL_MAX_LENGTH = 80;

/**
 * One piece of work somebody is proposing to take on.
 *
 * `taskType` is a workflow stage key, narrowed to the payable stages — the ones that are work a
 * person does rather than a state a garment sits in. The schema can only check that far; whether
 * THIS shop has the stage switched on is a question only the shop's own configuration answers,
 * and the service refuses a disabled stage with the same validation-shaped error.
 */
export const proposedTaskSchema = z.object({
  taskType: payableStageSchema,
  /** REQUIRED. Guessing on a customer's behalf is how a shop promises a date it cannot keep. */
  estimatedMinutes: estimatedMinutesSchema,
  priority: assignmentPrioritySchema.default(DEFAULT_ASSIGNMENT_PRIORITY),
  /** May this be spread across days, or must it happen in one sitting? */
  splittable: z.boolean().default(false),
  /** Extra minutes this task may buy on the day it lands on, for its own task type only. */
  otMinutes: otMinutesSchema.default(0),
  /** Free text for the screen — "Priya's lehenga". Never read by the calculation. */
  label: z.string().trim().min(1).max(CAPACITY_TASK_LABEL_MAX_LENGTH).optional(),
});
export type ProposedTask = z.infer<typeof proposedTaskSchema>;
export type ProposedTaskInput = z.input<typeof proposedTaskSchema>;

/**
 * The whole question.
 *
 * `weeklyOffDays` is a REQUEST field and not a stored setting, and that is not an oversight: this
 * module stores nothing at all, so it has nowhere to keep one. The counter says which days the
 * shop is shut, the same way it says which tasks it is proposing.
 */
export const capacityCheckRequestSchema = z.object({
  /** The day the customer is asking for, as a shop-timezone `YYYY-MM-DD`. */
  deliveryDay: shopDaySchema,
  tasks: z
    .array(proposedTaskSchema)
    .min(1, 'Propose at least one task')
    .max(CAPACITY_MAX_TASKS, `A check covers at most ${CAPACITY_MAX_TASKS} tasks`),
  weeklyOffDays: z
    .array(weekdaySchema)
    .max(7)
    .default([...DEFAULT_WEEKLY_OFF_DAYS]),
});
export type CapacityCheckRequest = z.infer<typeof capacityCheckRequestSchema>;
export type CapacityCheckInput = z.input<typeof capacityCheckRequestSchema>;

/* -------------------------------------------------------------------------------------------
 * The response
 * ---------------------------------------------------------------------------------------- */

/** A task type as this shop names it. No caller may hardcode an English stage label. */
export const capacityTaskTypeSchema = z.object({
  taskType: payableStageSchema,
  label: z.string(),
});
export type CapacityTaskType = z.infer<typeof capacityTaskTypeSchema>;

/**
 * One task type on one day.
 *
 * `overtimeMinutes` is how much of what was charged sat ABOVE the day's base capacity, so
 * `committedMinutes + proposedMinutes` already includes it. `freeMinutes` is what is left of the
 * base capacity and never goes below zero.
 */
export const capacityTaskTypeLoadSchema = z.object({
  taskType: payableStageSchema,
  label: z.string(),
  capacityMinutes: z.number().int().nonnegative(),
  committedMinutes: z.number().int().nonnegative(),
  proposedMinutes: z.number().int().nonnegative(),
  overtimeMinutes: z.number().int().nonnegative(),
  freeMinutes: z.number().int().nonnegative(),
});
export type CapacityTaskTypeLoad = z.infer<typeof capacityTaskTypeLoadSchema>;

/** One calendar day, whether or not the shop opens on it. */
export const capacityDaySchema = z.object({
  day: shopDaySchema,
  /** 0 is Sunday, matching `weeklyOffDays`. */
  weekday: weekdaySchema,
  /** False on a weekly off day: zero capacity, and no work is ever placed on it. */
  workingDay: z.boolean(),
  capacityMinutes: z.number().int().nonnegative(),
  committedMinutes: z.number().int().nonnegative(),
  proposedMinutes: z.number().int().nonnegative(),
  overtimeMinutes: z.number().int().nonnegative(),
  freeMinutes: z.number().int().nonnegative(),
  /** Every enabled task type, in workflow order — including the ones with nothing on them. */
  taskTypes: z.array(capacityTaskTypeLoadSchema),
});
export type CapacityDay = z.infer<typeof capacityDaySchema>;

/** Why a proposed task found no room. */
export const CAPACITY_UNPLACED_REASONS = ['no_capacity', 'no_single_day'] as const;
export type CapacityUnplacedReason = (typeof CAPACITY_UNPLACED_REASONS)[number];
export const capacityUnplacedReasonSchema = z.enum(CAPACITY_UNPLACED_REASONS);

/**
 * A proposed task that did not fit by the requested day.
 *
 * `no_single_day` is the answer that only a NON-splittable task can get: there were enough free
 * minutes across the window, but no one day held enough of them at once. That distinction is the
 * entire point of the `splittable` flag, so the response names it rather than reporting both
 * failures as "no capacity".
 */
export const unplacedTaskSchema = z.object({
  /** Position in the request's `tasks` array, so a screen can point at the row. */
  taskIndex: z.number().int().nonnegative(),
  taskType: payableStageSchema,
  taskTypeLabel: z.string(),
  label: z.string().nullable(),
  requestedMinutes: z.number().int().positive(),
  /** How much of it did fit — non-zero only for a splittable task that ran out part way. */
  placedMinutes: z.number().int().nonnegative(),
  shortfallMinutes: z.number().int().positive(),
  reason: capacityUnplacedReasonSchema,
});
export type UnplacedTask = z.infer<typeof unplacedTaskSchema>;

/**
 * The one task type most in the way. An infeasible answer always names one — "we cannot" without
 * "because the stitching table is short four hours" is not an answer a shop can act on.
 */
export const capacityBottleneckSchema = z.object({
  taskType: payableStageSchema,
  label: z.string(),
  shortfallMinutes: z.number().int().positive(),
});
export type CapacityBottleneck = z.infer<typeof capacityBottleneckSchema>;

/**
 * What the engine had to assume to answer at all — so the screen can caveat the verdict rather
 * than presenting a derived number as a measured one.
 */
export const capacityAssumptionsSchema = z.object({
  /** From the shop's attendance settings, or the shared defaults when it has stored none. */
  workdayStart: z.string(),
  workdayEnd: z.string(),
  workdayMinutes: z.number().int().nonnegative(),
  /** Echoed back, so the screen shows the same week the answer was computed against. */
  weeklyOffDays: z.array(weekdaySchema),
  /** Active people with at least one enabled specialisation — everybody else supplies nothing. */
  staffCounted: z.number().int().nonnegative(),
  /** Open assignments charged against the calendar before any proposed task was placed. */
  committedAssignmentCount: z.number().int().nonnegative(),
  /** How many of those carried no estimate at all. */
  unestimatedAssignmentCount: z.number().int().nonnegative(),
  /** What each of those was charged. Always {@link UNESTIMATED_ASSIGNMENT_MINUTES}, never zero. */
  unestimatedAssignmentMinutes: z.number().int().positive(),
  /** How far forward the search for a deliverable day was allowed to walk. */
  horizonDays: z.number().int().positive(),
});
export type CapacityAssumptions = z.infer<typeof capacityAssumptionsSchema>;

/** The verdict. */
export const capacityCheckResultSchema = z.object({
  feasible: z.boolean(),
  requestedDay: shopDaySchema,
  /**
   * The last day any proposed task occupies in the plan that delivers everything — the day the
   * shop could actually promise. `null` when nothing within the horizon delivers it: a fabricated
   * date is worse than an admission.
   */
  earliestDeliveryDay: shopDaySchema.nullable(),
  /** Today through the requested day, or through the earliest deliverable day if that is later. */
  days: z.array(capacityDaySchema),
  /** Non-null exactly when `feasible` is false. */
  bottleneck: capacityBottleneckSchema.nullable(),
  /** Total unmet minutes at the requested day, across every task type. */
  shortfallMinutes: z.number().int().nonnegative(),
  unplacedTasks: z.array(unplacedTaskSchema),
  /** The shop's enabled payable stages, in workflow order, with the shop's own labels. */
  taskTypes: z.array(capacityTaskTypeSchema),
  assumptions: capacityAssumptionsSchema,
});
export type CapacityCheckResult = z.infer<typeof capacityCheckResultSchema>;

/**
 * The order proposed work is placed in. Highest first; ties fall back to the order the request
 * listed them in, so the same question always gets the same answer.
 */
export const CAPACITY_PRIORITY_RANK: Readonly<Record<string, number>> = Object.freeze({
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
});

/** Handy for a screen that wants to colour the busiest stage. Zero capacity reads as fully used. */
export function taskTypeUtilisation(load: CapacityTaskTypeLoad): number {
  const charged = load.committedMinutes + load.proposedMinutes;
  if (load.capacityMinutes <= 0) return charged > 0 ? 1 : 0;
  return Math.min(1, charged / load.capacityMinutes);
}

/** A stable key for a per-day-per-type cell, for a table that needs one. */
export function capacityCellKey(day: string, taskType: PayableStage): string {
  return `${day}:${taskType}`;
}
